import { useLocalStorage } from "@mantine/hooks";
import type { SupportedAgent } from "@src/gql/graphql";
import { useCallback } from "react";

export const AGENT_DEFAULT_PREFERENCE_STORAGE_KEY =
	"illumi-agent-default-preference";

export interface AgentDefaultPreference {
	agentId?: SupportedAgent;
	webSearchEnabled: boolean;
}

const DEFAULT_PREFERENCE: AgentDefaultPreference = {
	webSearchEnabled: false,
};

export function clearAgentDefaultPreferenceOnLogout(): void {
	try {
		localStorage.removeItem(AGENT_DEFAULT_PREFERENCE_STORAGE_KEY);
	} catch {
		// localStorage may be unavailable
	}
}

export function useAgentDefaultPreference() {
	const [preference, setPreference] = useLocalStorage<AgentDefaultPreference>({
		key: AGENT_DEFAULT_PREFERENCE_STORAGE_KEY,
		defaultValue: DEFAULT_PREFERENCE,
		getInitialValueInEffect: false,
		sync: false,
	});

	const setDefaultAgentId = useCallback(
		(agentId: SupportedAgent) => {
			setPreference((prev) => ({ ...prev, agentId }));
		},
		[setPreference],
	);

	const setDefaultWebSearchEnabled = useCallback(
		(webSearchEnabled: boolean) => {
			setPreference((prev) => ({ ...prev, webSearchEnabled }));
		},
		[setPreference],
	);

	return {
		defaultAgentId: preference.agentId,
		defaultWebSearchEnabled: preference.webSearchEnabled,
		setDefaultAgentId,
		setDefaultWebSearchEnabled,
	};
}
